import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingCart,
  PlusCircle,
  Eye,
  Tag,
  Clock,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";

// ---------------------------------------------------------------------------
// Keyframe animations injected once via a <style> tag
// ---------------------------------------------------------------------------

const ANIMATIONS = `
  @keyframes gradientShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes orbFloat {
    0%, 100% { transform: translateY(0px) scale(1); }
    50%       { transform: translateY(-14px) scale(1.04); }
  }
`;

// ---------------------------------------------------------------------------
// Revenue Hero Card
// ---------------------------------------------------------------------------

function RevenueHeroCard(): ReactNode {
  return (
    <div
      dir="rtl"
      style={{
        background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 40%, #ec4899 80%, #f43f5e 100%)",
        backgroundSize: "200% 200%",
        animation: "gradientShift 6s ease infinite",
        borderRadius: 20,
        padding: "2rem 2rem 1.75rem",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 20px 60px rgba(124,58,237,0.35)",
        minHeight: 180,
      }}
    >
      {/* Floating orbs */}
      <div
        style={{
          position: "absolute",
          top: -40,
          left: -40,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          backdropFilter: "blur(4px)",
          animation: "orbFloat 6s ease-in-out infinite",
          animationDelay: "0s",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -30,
          right: 60,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(4px)",
          animation: "orbFloat 6s ease-in-out infinite",
          animationDelay: "2s",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 20,
          left: "45%",
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(4px)",
          animation: "orbFloat 6s ease-in-out infinite",
          animationDelay: "4s",
        }}
      />

      {/* Content above orbs */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <p
          style={{
            color: "rgba(255,255,255,0.75)",
            fontSize: "0.8rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginBottom: "0.5rem",
          }}
        >
          סיכום הכנסות
        </p>
        <p
          style={{
            color: "#fff",
            fontSize: "3rem",
            fontWeight: 900,
            lineHeight: 1,
            marginBottom: "0.4rem",
          }}
        >
          ₪0
        </p>
        <p
          style={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.8rem",
            marginBottom: "1.25rem",
          }}
        >
          אין נתונים עדיין
        </p>

        {/* Sub-stats row */}
        <div
          style={{
            display: "flex",
            gap: "1.5rem",
            flexWrap: "wrap",
          }}
        >
          {[
            { label: "שינוי שבועי", value: "—" },
            { label: "הזמנות", value: "0" },
            { label: "ערך הזמנה ממוצע", value: "₪0" },
            { label: "לקוחות", value: "0" },
          ].map(({ label, value }) => (
            <div key={label}>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", marginBottom: "0.15rem" }}>
                {label}
              </p>
              <p style={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats Grid
// ---------------------------------------------------------------------------

interface StatItem {
  readonly emoji: string;
  readonly label: string;
  readonly value: string;
  readonly subtitle: string;
}

const STAT_ITEMS: readonly StatItem[] = [
  { emoji: "📦", label: "מוצרים פעילים", value: "0", subtitle: "לחץ להוסיף מוצרים" },
  { emoji: "👤", label: "לקוחות חדשים", value: "0", subtitle: "החודש הנוכחי" },
  { emoji: "💰", label: "הכנסות החודש", value: "₪0", subtitle: "אין נתונים עדיין" },
  { emoji: "🛒", label: "הזמנות היום", value: "0", subtitle: "אין הזמנות עדיין" },
];

function StatsGrid(): ReactNode {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "1rem",
      }}
    >
      {STAT_ITEMS.map((item) => (
        <div
          key={item.label}
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "1.25rem 1.25rem 1rem",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            cursor: "default",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.transform = "translateY(-3px)";
            (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
            (e.currentTarget as HTMLDivElement).style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";
          }}
        >
          <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>{item.emoji}</div>
          <p
            style={{
              fontSize: "0.78rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "hsl(220 9% 46%)",
              marginBottom: "0.3rem",
            }}
          >
            {item.label}
          </p>
          <p
            style={{
              fontSize: "1.85rem",
              fontWeight: 800,
              lineHeight: 1,
              color: "hsl(224 71% 4%)",
              marginBottom: "0.3rem",
            }}
          >
            {item.value}
          </p>
          <p style={{ fontSize: "0.75rem", color: "hsl(220 9% 56%)" }}>{item.subtitle}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Actions
// ---------------------------------------------------------------------------

interface QuickActionItem {
  readonly emoji: string;
  readonly label: string;
  readonly href: string;
}

const QUICK_ACTIONS: readonly QuickActionItem[] = [
  { emoji: "🛍️", label: "הזמנה חדשה", href: "/orders" },
  { emoji: "📦", label: "מוצר חדש", href: "/products/new" },
  { emoji: "🏷️", label: "קופון חדש", href: "/coupons/new" },
  { emoji: "👀", label: "צפייה במוצרים", href: "/products" },
];

function QuickActionsCard(): ReactNode {
  return (
    <Card style={{ borderRadius: 18, border: "1px solid hsl(220 13% 87%)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
      <div style={{ padding: "1.25rem 1.25rem 0.75rem" }}>
        <h2 style={{ fontSize: "0.9rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <span>⚡</span>
          פעולות מהירות
        </h2>
      </div>
      <CardContent style={{ padding: "0 1rem 1.25rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "0.5rem",
          }}
        >
          {QUICK_ACTIONS.map((action) => (
            <Link
              key={action.label}
              to={action.href}
              style={{ textDecoration: "none" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  padding: "0.9rem 0.5rem",
                  borderRadius: 14,
                  background: "hsl(220 14% 96%)",
                  border: "1px solid hsl(220 13% 90%)",
                  cursor: "pointer",
                  transition: "transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease",
                  minHeight: 80,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = "scale(1.04)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow = "0 6px 18px rgba(124,58,237,0.15)";
                  (e.currentTarget as HTMLDivElement).style.background = "hsl(234 89% 97%)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = "scale(1)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                  (e.currentTarget as HTMLDivElement).style.background = "hsl(220 14% 96%)";
                }}
              >
                <span style={{ fontSize: "1.35rem" }}>{action.emoji}</span>
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    textAlign: "center",
                    color: "hsl(224 71% 4%)",
                    lineHeight: 1.3,
                  }}
                >
                  {action.label}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent Activity
// ---------------------------------------------------------------------------

function RecentActivityCard(): ReactNode {
  return (
    <Card style={{ borderRadius: 18, border: "1px solid hsl(220 13% 87%)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.25rem 1.25rem 0.75rem",
        }}
      >
        <h2 style={{ fontSize: "0.9rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <Clock size={16} style={{ color: "hsl(199 89% 48%)" }} />
          פעילות אחרונה
        </h2>
        <Link to="/orders" style={{ textDecoration: "none" }}>
          <Button
            variant="ghost"
            size="sm"
            style={{ fontSize: "0.75rem", gap: 4, color: "hsl(220 9% 46%)", minHeight: 44 }}
          >
            כל ההזמנות
          </Button>
        </Link>
      </div>
      <CardContent style={{ padding: "0 1.25rem 1.5rem" }}>
        {/* Empty state */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2.5rem 1rem",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "hsl(220 14% 93%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "0.85rem",
            }}
          >
            <ShoppingCart size={26} style={{ color: "hsl(220 9% 56%)" }} />
          </div>
          <p style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.3rem" }}>
            אין הזמנות עדיין
          </p>
          <p style={{ fontSize: "0.78rem", color: "hsl(220 9% 56%)", maxWidth: 200, lineHeight: 1.5 }}>
            כשיגיעו הזמנות, הן יופיעו כאן
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Foxy AI Teaser
// ---------------------------------------------------------------------------

function FoxyAICard(): ReactNode {
  return (
    <Card
      style={{
        borderRadius: 18,
        border: "1px solid hsl(220 13% 87%)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Soft amber glow overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(135deg, rgba(251,191,36,0.04) 0%, rgba(249,115,22,0.04) 100%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.25rem 1.25rem 0.75rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        <h2 style={{ fontSize: "0.9rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "1rem" }}>🦊</span>
          פוקסי — עוזר AI
        </h2>
        <Link to="/foxy" style={{ textDecoration: "none" }}>
          <Button
            variant="ghost"
            size="sm"
            style={{ fontSize: "0.75rem", gap: 4, color: "hsl(220 9% 46%)", minHeight: 44 }}
          >
            פתח שיחה
          </Button>
        </Link>
      </div>

      <CardContent style={{ padding: "0 1.25rem 1.5rem", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.85rem" }}>
          {/* Fox avatar */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              background: "linear-gradient(135deg, #fbbf24, #f97316)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.3rem",
              flexShrink: 0,
              boxShadow: "0 4px 12px rgba(251,191,36,0.4)",
            }}
          >
            🦊
          </div>

          <div style={{ flex: 1 }}>
            {/* Chat bubble */}
            <div
              style={{
                background: "hsl(220 14% 94%)",
                borderRadius: "14px 4px 14px 14px",
                padding: "0.75rem 1rem",
                fontSize: "0.85rem",
                color: "hsl(224 71% 4%)",
                lineHeight: 1.55,
                marginBottom: "0.85rem",
              }}
            >
              שלום! אני פוקסי, עוזר ה-AI שלך. אפשר לשאול אותי שאלות על העסק שלך, לקבל עצות מכירות ועוד.
            </div>

            {/* CTA button */}
            <Link to="/foxy" style={{ textDecoration: "none", display: "inline-block" }}>
              <button
                style={{
                  background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "0 1.25rem",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  boxShadow: "0 4px 14px rgba(124,58,237,0.3)",
                  transition: "opacity 0.15s ease, transform 0.15s ease",
                  height: 44,
                  minHeight: 44,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.opacity = "0.9";
                  (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.opacity = "1";
                  (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                }}
              >
                <span>💬</span>
                שאל את פוקסי
              </button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Dashboard page
// ---------------------------------------------------------------------------

export function DashboardPage(): ReactNode {
  const { user } = useAuth();

  const hour = new Date().getHours();
  const greeting =
    hour < 12
      ? "בוקר טוב"
      : hour < 17
        ? "צהריים טובים"
        : "ערב טוב";

  const displayName = user?.name ?? "משתמש";

  return (
    <div dir="rtl" style={{ padding: "1.5rem", maxWidth: 1400 }}>
      {/* Inject keyframe animations */}
      <style>{ANIMATIONS}</style>

      {/* Page title */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h1
          style={{
            fontSize: "clamp(1.5rem, 3vw, 2rem)",
            fontWeight: 900,
            lineHeight: 1.2,
            background: "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            display: "inline-block",
            marginBottom: "0.3rem",
          }}
        >
          {greeting}, {displayName} 👋
        </h1>
        <p style={{ fontSize: "0.875rem", color: "hsl(220 9% 46%)" }}>
          הנה סיכום הפעילות שלך היום
        </p>
      </div>

      {/* Revenue Hero */}
      <div style={{ marginBottom: "1.25rem" }}>
        <RevenueHeroCard />
      </div>

      {/* Stats Grid */}
      <div style={{ marginBottom: "1.25rem" }}>
        <StatsGrid />
      </div>

      {/* Quick Actions — full width row */}
      <div style={{ marginBottom: "1.25rem" }}>
        <QuickActionsCard />
      </div>

      {/* Bottom two-column: Activity + Foxy */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "1.25rem",
        }}
      >
        <RecentActivityCard />
        <FoxyAICard />
      </div>

      {/* Extra bottom padding for mobile nav */}
      <div style={{ height: "1.5rem" }} />
    </div>
  );
}
